export const SENSITIVE_STUDENTS_STATUS_FILTERS = [
  'active',
  'ended',
] as const;

export type SensitiveStudentsStatusFilter = (typeof SENSITIVE_STUDENTS_STATUS_FILTERS)[number];

export const SENSITIVE_STUDENTS_STATUS_LABELS: Record<SensitiveStudentsStatusFilter, string> = {
  active: 'Sensível',
  ended: 'Encerrada',
};

export interface SensitiveStudentsDashboardQuery {
  searchName: string;
  status: SensitiveStudentsStatusFilter | '';
  page: number;
  pageSize: number;
}

export interface SensitiveStudentDashboardRow {
  periodId: number;
  studentId: number;
  studentName: string;
  coachName: string | null;
  reason: string;
  startedAt: string;
  endedAt: string | null;
  endJustification: string | null;
  sensitivityDays: number;
  ended: boolean;
}

export interface SensitiveStudentsDashboard {
  page: number;
  pageSize: number;
  totalCount: number;
  students: SensitiveStudentDashboardRow[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePositiveInt(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return null;
  }

  return value;
}

function parseNonNegativeInt(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    return null;
  }

  return value;
}

function parseRow(value: unknown): SensitiveStudentDashboardRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const periodId = parsePositiveInt(value.periodId);
  const studentId = parsePositiveInt(value.studentId);
  const studentName = typeof value.studentName === 'string' ? value.studentName.trim() : '';
  const reason = typeof value.reason === 'string' ? value.reason.trim() : '';
  const startedAt = typeof value.startedAt === 'string' ? value.startedAt.trim() : '';
  const sensitivityDays = parseNonNegativeInt(value.sensitivityDays);

  if (
    periodId === null
    || studentId === null
    || !studentName
    || !reason
    || !startedAt
    || sensitivityDays === null
  ) {
    return null;
  }

  const endedAt = typeof value.endedAt === 'string' && value.endedAt.trim()
    ? value.endedAt.trim()
    : null;
  const endJustification = typeof value.endJustification === 'string' && value.endJustification.trim()
    ? value.endJustification.trim()
    : null;

  return {
    periodId,
    studentId,
    studentName,
    coachName: typeof value.coachName === 'string' && value.coachName.trim()
      ? value.coachName.trim()
      : null,
    reason,
    startedAt,
    endedAt,
    endJustification,
    sensitivityDays,
    ended: value.ended === true || endedAt != null,
  };
}

export function parseSensitiveStudentsDashboard(body: unknown): SensitiveStudentsDashboard | null {
  if (!isRecord(body) || !Array.isArray(body.students)) {
    return null;
  }

  const page = parsePositiveInt(body.page);
  const pageSize = parsePositiveInt(body.pageSize);
  const totalCount = typeof body.totalCount === 'number' && Number.isInteger(body.totalCount) && body.totalCount >= 0
    ? body.totalCount
    : null;

  if (page === null || pageSize === null || totalCount === null) {
    return null;
  }

  const students: SensitiveStudentDashboardRow[] = [];
  for (const item of body.students) {
    const row = parseRow(item);
    if (row) {
      students.push(row);
    }
  }

  return {
    page,
    pageSize,
    totalCount,
    students,
  };
}

const HOUR_MS = 60 * 60 * 1000;

/** Elapsed time from when the period started until end (or now if still open). */
export function formatSensitivityDuration(
  startedAt: string,
  endedAt: string | null,
  now = new Date(),
): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : now.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return '0 Horas';
  }

  const totalHours = Math.max(0, Math.floor((end - start) / HOUR_MS));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const dayLabel = days === 1 ? 'Dia' : 'Dias';
  const hourLabel = hours === 1 ? 'Hora' : 'Horas';

  if (days > 0 && hours > 0) {
    return `${days} ${dayLabel} ${hours} ${hourLabel}`;
  }

  if (days > 0) {
    return `${days} ${dayLabel}`;
  }

  return `${hours} ${hourLabel}`;
}
